import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart, Moon, Music, Compass, Ticket, AlertTriangle, CheckCircle, Coffee, Anchor, Sunset, Users, DollarSign, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const JinjaNightlife = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Jinja Nightlife 2025: The ONLY Guide You Need | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Jinja Nightlife 2025: The ONLY Guide You Need";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
            alt="Jinja Nightlife"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/ugLHrmP.jpg"
                alt="Ankunda Prima"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Ankunda Prima</h3>
                <p className="text-white/80 text-sm">Nightlife Expert</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-purple-500/20 rounded-full px-2.5 py-0.5 text-xs text-purple-400 mb-4 backdrop-blur-sm">
                <Moon className="h-3 w-3 mr-1" />
                Nightlife Guide
              </div>
              
              <h1 className="text-4xl font-bold">Jinja Nightlife 2025: The ONLY Guide You Need</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  April 20, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  15 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Jinja, Uganda
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Welcome to the definitive guide to Jinja's vibrant nightlife scene for 2025. As Uganda's adventure capital transforms after dark, this comprehensive resource covers everything from riverside bars and exclusive clubs to unique night activities and essential safety tips. Whether you're a solo traveler, backpacker, or nightlife enthusiast, this guide ensures you'll experience the best of Jinja after sunset.
                </p>

                <h2>Why Jinja's Nightlife Will Surprise You</h2>
                
                <p>
                  Jinja's nightlife scene has undergone a remarkable transformation in recent years. Once overshadowed by Kampala's bustling club scene, Jinja has carved out its own unique after-dark identity that perfectly complements its daytime adventure reputation.
                </p>

                <p>
                  What makes Jinja's nightlife special is its seamless blend of natural beauty, international influence, and authentic Ugandan culture. Unlike the more commercial and sometimes overwhelming Kampala scene, Jinja offers a more laid-back yet equally vibrant experience where the mighty Nile River serves as both backdrop and inspiration.
                </p>

                <p>
                  The city's position as Uganda's adventure tourism hub has attracted a diverse mix of international travelers, expats, and locals, creating a uniquely cosmopolitan atmosphere in this relatively small riverside city. This cultural fusion is reflected in everything from the music played at venues to the creative cocktails and fusion food offerings.
                </p>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80" 
                    alt="Jinja nightlife scene"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Jinja's vibrant nightlife offers a perfect mix of local culture and international influence
                  </p>
                </div>

                <h2>The 5 Must-Visit Jinja Night Spots (Ranked)</h2>

                <p>
                  After extensive research (and many late nights), I've compiled the definitive ranking of Jinja's best nightlife venues for 2025:
                </p>

                <h3>1. The Black Lantern</h3>
                <p>
                  This atmospheric riverside bar has earned its top spot through a perfect combination of stunning location, creative mixology, and vibrant atmosphere. Set in a converted colonial-era building with a sprawling deck overlooking the Nile, The Black Lantern transforms from a relaxed sundowner spot to Jinja's most sophisticated nightspot as darkness falls.
                </p>
                <p>
                  <strong>Signature Cocktail:</strong> "Nile Moonrise" – A color-changing gin-based creation with local botanicals and butterfly pea flower
                </p>
                <p>
                  <strong>Best Night:</strong> Thursdays for live acoustic music and half-price cocktails
                </p>
                <p>
                  <strong>Insider Tip:</strong> The hidden back garden area offers more intimate seating and often features impromptu jam sessions with local musicians
                </p>

                <h3>2. Brisk Bar</h3>
                <p>
                  The newest addition to Jinja's nightlife scene has quickly established itself as a must-visit destination. This sleek, modern venue brings a touch of international nightclub flair to the riverside city, with state-of-the-art sound systems and lighting.
                </p>
                <p>
                  <strong>Signature Experience:</strong> Thursday Afrobeat nights featuring Uganda's hottest DJs
                </p>
                <p>
                  <strong>Best Night:</strong> Saturday for their famous "Brisk Beats" party that runs until 5 AM
                </p>
                <p>
                  <strong>Insider Tip:</strong> The rooftop section offers breathtaking views and a more relaxed atmosphere than the high-energy main floor
                </p>

                <h3>3. Source of the Nile Rooftop</h3>
                <p>
                  Perched atop the Nile Hotel, this stylish rooftop bar offers 360° views of Jinja and the Nile River. The sophisticated atmosphere attracts a mix of travelers and Ugandan professionals.
                </p>
                <p>
                  <strong>Signature Drink:</strong> "Sunset Spritz" – A refreshing prosecco-based cocktail with local hibiscus
                </p>
                <p>
                  <strong>Best Night:</strong> Friday for their famous sundowner sessions with live DJ
                </p>
                <p>
                  <strong>Insider Tip:</strong> Arrive before sunset to secure the best tables, as they don't take reservations
                </p>

                <h3>4. Nile Beats Club</h3>
                <p>
                  For those looking to dance until dawn, Nile Beats Club delivers the most authentic Ugandan clubbing experience in Jinja. This high-energy venue pumps out a mix of Afrobeats, dancehall, and international hits.
                </p>
                <p>
                  <strong>Signature Experience:</strong> Monthly "Beats & Bites" events combining street food vendors with guest DJs
                </p>
                <p>
                  <strong>Best Night:</strong> Saturday when the club is at its busiest and most energetic
                </p>
                <p>
                  <strong>Insider Tip:</strong> Ladies get free entry before 11 PM on Fridays
                </p>

                <h3>5. Riverside Lounge</h3>
                <p>
                  This laid-back venue offers a more relaxed alternative to Jinja's higher-energy spots. With comfortable seating, board games, and a excellent craft beer selection, it's perfect for meaningful conversations and chilled evenings.
                </p>
                <p>
                  <strong>Signature Drink:</strong> Their rotating selection of Ugandan craft beers
                </p>
                <p>
                  <strong>Best Night:</strong> Wednesday for their popular pub quiz night
                </p>
                <p>
                  <strong>Insider Tip:</strong> The kitchen serves food until 1 AM, making it a great late-night dining option
                </p>

                <div className="bg-purple-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="h-5 w-5 text-purple-600 mr-2" />
                    People Also Ask
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Is Jinja safe at night?</h4>
                      <p className="text-gray-700 mt-1">
                        Yes, Jinja is generally safe at night, especially in the main tourist areas. However, standard safety precautions should be followed: use trusted transportation, stay in groups, and keep valuables secure.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">What time do clubs close in Jinja?</h4>
                      <p className="text-gray-700 mt-1">
                        Most bars in Jinja close around 2 AM on weekdays and 4 AM on weekends. A few venues like Nile Beats Club and Brisk Bar have special licenses to operate until 5 AM on Fridays and Saturdays.
                      </p>
                    </div>
                  </div>
                </div>

                <h2>Secret Local Experience: Street Food Crawl</h2>

                <p>
                  One of Jinja's best-kept nightlife secrets is its vibrant street food scene that comes alive after dark. For an authentic local experience, follow this mini food crawl itinerary:
                </p>

                <h3>7PM: Rolex at Main Street Market</h3>
                <p>
                  Begin your culinary adventure with Uganda's famous street food – the rolex. This egg and vegetable wrap (rolled + eggs = rolex) is the perfect foundation for a night of exploration. The vendors near Main Street Market make some of Jinja's best.
                </p>

                <h3>10PM: Muchomo Pork Skewers</h3>
                <p>
                  As the night progresses, head to the muchomo stands near the taxi park. These grilled meat skewers are a Ugandan nightlife staple, perfectly seasoned and cooked over open flames. Pair with a cold Nile Special beer for the full experience.
                </p>

                <h3>Midnight: Late-Night Katogo</h3>
                <p>
                  For a truly local experience, find one of the late-night katogo spots. This hearty dish of matoke (plantains) mixed with beef or offal in a rich sauce is Uganda's favorite late-night meal – perfect for soaking up the evening's indulgences.
                </p>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" 
                    alt="Jinja street food at night"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Jinja's vibrant street food scene comes alive after dark
                  </p>
                </div>

                <h2>Adventure After Dark: Unique Jinja Night Activities</h2>

                <p>
                  Jinja's nightlife extends far beyond bars and clubs. Here are some unique after-dark experiences that set this city apart:
                </p>

                <h3>Moonlight Bungee Jumping</h3>
                <p>
                  For adrenaline junkies, Nile High Bungee now offers nighttime jumps on Friday and Saturday nights. The 44-meter plunge is illuminated by powerful spotlights, creating an unforgettable (and highly Instagrammable) experience.
                </p>
                <p>
                  <strong>Cost:</strong> $120 per person
                </p>
                <p>
                  <strong>Times:</strong> 7 PM to 10 PM (last jump)
                </p>
                <p>
                  <strong>Booking:</strong> Advance reservation required; includes free drink at the Adrift Bar
                </p>

                <h3>Private Yacht Parties</h3>
                <p>
                  Several operators now offer evening yacht charters on the Nile, complete with onboard sound systems, bartenders, and catering options. These floating parties have become increasingly popular for special occasions.
                </p>
                <p>
                  <strong>Capacity:</strong> 15-20 passengers
                </p>
                <p>
                  <strong>Cost:</strong> From $300 for a 3-hour charter (basic package)
                </p>
                <p>
                  <strong>Booking:</strong> Minimum 48 hours in advance
                </p>

                <h3>Night Market Tours</h3>
                <p>
                  The newly established Jinja Night Market is a sensory delight, with dozens of food stalls, craft vendors, and impromptu music performances. Guided tours help you navigate the best stalls and hidden gems.
                </p>
                <p>
                  <strong>Cost:</strong> $25 per person (includes food tastings)
                </p>
                <p>
                  <strong>Times:</strong> Tours depart at 8 PM and 9:30 PM
                </p>
                <p>
                  <strong>Duration:</strong> Approximately 2 hours
                </p>

                <h2>Jinja Nightlife Safety: 7 Rules Every Visitor Should Know</h2>

                <p>
                  While Jinja is generally safe for nightlife enthusiasts, following these essential rules will ensure your experience remains positive:
                </p>

                <ol>
                  <li>
                    <strong>Always use SafeBoda or trusted transportation</strong> – Avoid random taxis or motorcycle drivers, especially late at night. The SafeBoda app connects you with verified drivers who provide helmets.
                  </li>
                  <li>
                    <strong>Be wary of clubs requiring ID scans</strong> – Legitimate venues check IDs visually; those scanning or photographing IDs may be collecting data improperly.
                  </li>
                  <li>
                    <strong>Stick to the Main Street and riverside areas</strong> – These tourist-friendly zones are well-lit and have regular security patrols.
                  </li>
                  <li>
                    <strong>Use the buddy system</strong> – Never leave a venue alone, especially after midnight.
                  </li>
                  <li>
                    <strong>Keep a backup phone battery</strong> – Power outages are common, and you don't want to be stranded without a way to call transportation.
                  </li>
                  <li>
                    <strong>Carry only essential cash</strong> – Most venues are cash-only, but don't bring more than you need for the night.
                  </li>
                  <li>
                    <strong>Trust your instincts</strong> – If a situation feels unsafe, it probably is. Don't hesitate to leave and find another venue.
                  </li>
                </ol>

                <div className="bg-yellow-50 p-6 rounded-lg my-8 border-l-4 border-yellow-500">
                  <div className="flex">
                    <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Safety Note</h3>
                      <p className="text-yellow-700">
                        While exploring Jinja's nightlife, be aware that some venues may advertise "special experiences" that are actually illegal activities. Legitimate nightlife businesses will never offer drugs or other illicit services. Report any suspicious activity to local authorities.
                      </p>
                    </div>
                  </div>
                </div>

                <h2>How to Book Your Jinja Night Crawl</h2>

                <p>
                  For the safest and most comprehensive Jinja nightlife experience, consider booking a guided night crawl package. These include:
                </p>

                <ul>
                  <li>Expert local guide who knows the best spots</li>
                  <li>Skip-the-line VIP entry to all venues</li>
                  <li>Secure transportation between locations</li>
                  <li>Welcome drinks at each venue</li>
                  <li>Street food tastings</li>
                  <li>24/7 emergency support</li>
                </ul>

                <div className="bg-ta-green/10 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Ticket className="h-5 w-5 text-ta-green mr-2" />
                    Book Your Jinja Night Crawl Experience
                  </h3>
                  <p className="text-gray-700 mb-4">
                    TravelCord offers several Jinja nightlife packages to suit different preferences and budgets. Book now to secure your spot – weekend tours often sell out days in advance!
                  </p>
                  <Link 
                    to="/Udanga_Nightlife_Ultimate_Guide"
                    className="inline-flex items-center px-4 py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    View Available Packages
                  </Link>
                </div>

                <h2>Traveler Testimonials</h2>

                <div className="space-y-6 my-8">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 italic">
                      "The Jinja night crawl was the unexpected highlight of my Uganda trip! Our guide knew all the best spots and got us VIP treatment everywhere. The moonlight bungee jump was terrifying but absolutely worth it!"
                    </p>
                    <div className="flex items-center mt-3">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="ml-2 text-sm text-gray-600">Sarah M. (@sarahadventures)</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 italic">
                      "As a solo female traveler, I was hesitant about exploring Jinja's nightlife alone. The guided night crawl was perfect – I felt completely safe while still having an authentic experience. The street food tour was a delicious bonus!"
                    </p>
                    <div className="flex items-center mt-3">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="ml-2 text-sm text-gray-600">Emma L. (@emmatravels2025)</span>
                    </div>
                  </div>
                </div>

                <h2>Conclusion: Experience Jinja After Dark</h2>

                <p>
                  Jinja's nightlife scene offers a refreshing alternative to the more commercialized experiences found in larger cities. With its unique blend of riverside charm, international influence, and authentic Ugandan hospitality, a night out in Jinja should be on every traveler's Uganda itinerary.
                </p>

                <p>
                  Whether you're dancing under the stars at a rooftop bar, enjoying a sunset cruise on the Nile, or sampling local street food at the night market, Jinja after dark promises unforgettable memories and experiences that showcase a different side of Uganda's adventure capital.
                </p>

                <p>
                  For more detailed information on specific venues, upcoming events, or to book a guided night crawl experience, visit our <Link to="/Udanga_Nightlife_Ultimate_Guide" className="text-ta-green hover:underline">Jinja Nightlife Packages</Link> page or check out our <Link to="/blog/jinja-night-crawling" className="text-ta-green hover:underline">Ultimate Jinja Night Crawling Experience</Link> guide.
                </p>

                <p>
                  Which Jinja night spot are you most excited to visit? Tag us in your adventures using #JinjaNights and #TravelCordUganda!
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Jinja nightlife 2025", "Nile boat party", "Jinja bar crawl", "Uganda night clubs", "safe nightlife Jinja", "Jinja after dark", "best bars near Nile"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-purple-100 hover:text-purple-800 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="Jinja Nightlife 2025: The ONLY Guide You Need"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="Jinja Nightlife 2025: The ONLY Guide You Need"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="Jinja Nightlife 2025: The ONLY Guide You Need"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/ugLHrmP.jpg"
                      alt="Ankunda Prima"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Ankunda Prima</h3>
                      <p className="text-gray-600">Nightlife Expert</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Ankunda has been exploring Uganda's nightlife scene for over 6 years and specializes in connecting travelers with authentic local experiences. Her expertise in Jinja's evolving night scene makes her the perfect guide for your after-dark adventures.
                  </p>
                  <Link to="/guides/3" className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors block text-center">
                    View Guide Profile
                  </Link>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">In This Article</h3>
                  <nav className="space-y-2">
                    <a href="#why-jinja-nightlife" className="block text-gray-600 hover:text-purple-600">Why Jinja's Nightlife Will Surprise You</a>
                    <a href="#must-visit-spots" className="block text-gray-600 hover:text-purple-600">5 Must-Visit Night Spots</a>
                    <a href="#street-food" className="block text-gray-600 hover:text-purple-600">Secret Local Experience: Street Food</a>
                    <a href="#night-activities" className="block text-gray-600 hover:text-purple-600">Adventure After Dark</a>
                    <a href="#safety-rules" className="block text-gray-600 hover:text-purple-600">7 Safety Rules</a>
                    <a href="#booking" className="block text-gray-600 hover:text-purple-600">How to Book</a>
                    <a href="#testimonials" className="block text-gray-600 hover:text-purple-600">Traveler Testimonials</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/jinja-night-crawling" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
                          alt="Jinja Night Crawling"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                            The Ultimate Jinja Night Crawling Experience
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">12 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/kampala-nightlife-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80"
                          alt="Kampala Nightlife Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                            Kampala Nightlife Guide: Best Clubs, Bars & Entertainment
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-purple-50 rounded-lg border border-purple-100 p-6">
                  <div className="flex items-center mb-4">
                    <Moon className="h-6 w-6 text-purple-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Book a Night Crawl</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Experience Jinja's vibrant nightlife with our expert guides. VIP access, transportation, and drinks included!
                  </p>
                  <Link 
                    to="/Udanga_Nightlife_Ultimate_Guide"
                    className="block w-full py-2 bg-purple-600 text-white text-center rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    View Nightlife Packages
                  </Link>
                </div>

                {/* FAQ Box */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Frequently Asked Questions</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900">What should I wear for a night out in Jinja?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Jinja's nightlife is generally casual. Smart casual (nice jeans and a collared shirt/nice top) is appropriate for most venues. Only the most upscale spots like Source of the Nile Rooftop have stricter dress codes.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">How much should I budget for a night out?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Budget $30-50 for a full night including transportation, drinks, and food. Premium venues and experiences like boat parties will cost more.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Is it safe for solo female travelers?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Yes, with proper precautions. We recommend joining a guided night crawl for solo travelers for added safety and a better overall experience.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default JinjaNightlife;